import { t } from './i18n/i18n';
import { boostIconHtml, boostLines, boostStore, boostTitle, formatBoostTime, liveBoosts } from './boosts';
import type { BoostView } from '../screens/shop/shop-api';
import { formatMarchClock, marchProgress, marchStore, type MarchView } from './marches';
import { icon } from './icons';

/**
 * Small chips for the player's running boosts AND marches on the road, mounted over the colony view and the world map.
 * Each shows its icon with a short timer (hours, then minutes once under an hour; "N×" for the battle-counted
 * attack boost) and, on hover or keyboard focus, a tooltip: icon, name, what it does, time left.
 *
 * The tooltip lives on <body> (position: fixed) like the colony view's building tooltip, so no screen's
 * overflow or 3D transform can clip or skew it.
 */

const TICK_MS = 30_000;

function timerText(b: BoostView, now: number): string {
  if (b.usesRemaining != null) return `${b.usesRemaining}×`;
  return formatBoostTime(new Date(b.endsAt).getTime() - now);
}

function timeLeftText(b: BoostView, now: number): string {
  if (b.usesRemaining != null) return t('boost.ATTACK.uses', { n: String(b.usesRemaining) });
  return t('boost.timeLeft', { time: formatBoostTime(new Date(b.endsAt).getTime() - now) });
}

export function mountBoostHud(host: HTMLElement): () => void {
  host.classList.add('boost-hud');
  host.setAttribute('role', 'group');
  host.setAttribute('aria-label', t('boost.hudLabel'));

  let tip: HTMLElement | null = null;
  let tipFor: string | null = null;

  function hideTip(): void {
    tip?.remove();
    tip = null;
    tipFor = null;
  }

  /** The HUD rides the LEFT edge of the screen, so the popup opens to the right of its chip (toward the middle of the view), vertically centred on it; if there is no room that side it flips to the left. */
  function positionTip(target: HTMLElement): void {
    if (!tip) return;
    const r = target.getBoundingClientRect();
    const tr = tip.getBoundingClientRect();
    const roomRight = r.right + 12 + tr.width <= window.innerWidth - 8;
    const left = roomRight ? r.right + 12 : Math.max(8, r.left - 12 - tr.width);
    const top = Math.min(Math.max(8, r.top + r.height / 2 - tr.height / 2), window.innerHeight - tr.height - 8);
    tip.style.left = `${left}px`;
    tip.style.top = `${top}px`;
  }

  function showTip(target: HTMLElement, b: BoostView): void {
    hideTip();
    const now = Date.now();
    tip = document.createElement('div');
    tip.className = 'boost-tip fixed z-[65] pointer-events-none';
    tip.setAttribute('role', 'tooltip');
    tip.innerHTML = `
      <div class="boost-tip-head">${boostIconHtml(b.type, 'boost-tip-icon')}<span>${escape(boostTitle(b.type, b.params))}</span></div>
      <ul class="boost-tip-lines">${boostLines(b.type, b.params, b.usesRemaining).map((l) => `<li>${escape(l)}</li>`).join('')}</ul>
      <div class="boost-tip-time">${escape(timeLeftText(b, now))}</div>`;
    document.body.appendChild(tip);
    tipFor = b.id;
    positionTip(target);
  }

  function escape(s: string): string {
    const d = document.createElement('div');
    d.textContent = s;
    return d.innerHTML;
  }

  let marches: MarchView[] = marchStore.get();

  function marchTitle(m: MarchView): string {
    return t(m.kind === 'outgoing' ? 'march.hud.outgoing' : 'march.hud.incoming', { name: m.otherName });
  }

  function showMarchTip(target: HTMLElement, m: MarchView): void {
    hideTip();
    tip = document.createElement('div');
    tip.className = 'boost-tip fixed z-[65] pointer-events-none';
    tip.setAttribute('role', 'tooltip');
    const arrives = new Date(m.arrivalAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    const lines = [
      m.kind === 'outgoing' ? t('march.hud.from', { name: m.ownName }) : t('march.hud.target', { name: m.ownName }),
      t('march.hud.distance', { count: m.distanceTiles }),
      t('march.hud.arrives', { time: arrives }),
    ];
    tip.innerHTML = `
      <div class="boost-tip-head">${icon(m.kind === 'outgoing' ? 'crosshair' : 'skull', 'boost-tip-icon')}<span>${escape(marchTitle(m))}</span></div>
      <ul class="boost-tip-lines">${lines.map((l) => `<li>${escape(l)}</li>`).join('')}</ul>
      <div class="boost-tip-time" data-march-tip-time>${escape(formatMarchClock(new Date(m.arrivalAt).getTime() - Date.now()))}</div>`;
    document.body.appendChild(tip);
    tipFor = m.id;
    positionTip(target);
  }

  function render(boosts: BoostView[]): void {
    const now = Date.now();
    const live = liveBoosts(boosts, now);
    const roads = marches.filter((m) => new Date(m.arrivalAt).getTime() > now);
    if (tipFor && !live.some((b) => b.id === tipFor) && !roads.some((m) => m.id === tipFor)) hideTip();
    host.hidden = live.length === 0 && roads.length === 0;
    const marchChips = roads
      .map(
        (m) => `
        <span class="boost-chip boost-chip--march boost-chip--${m.kind}" tabindex="0" data-march-id="${m.id}"
          aria-label="${escape(`${marchTitle(m)}. ${t('march.hud.arrives', { time: new Date(m.arrivalAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) })}`)}">
          ${icon(m.kind === 'outgoing' ? 'crosshair' : 'skull', 'boost-chip-icon')}
          <span class="boost-chip-main">
            <span class="boost-chip-name">${escape(m.otherName)}</span>
            <span class="boost-chip-time" data-march-time>${formatMarchClock(new Date(m.arrivalAt).getTime() - now)}</span>
          </span>
          <span class="boost-chip-bar" aria-hidden="true"><span data-march-bar style="width:${marchProgress(m, now).toFixed(1)}%"></span></span>
        </span>`,
      )
      .join('');
    const boostChips = live
      .map(
        (b) => `
        <span class="boost-chip" tabindex="0" data-boost-id="${b.id}" data-boost-type="${b.type}"
          aria-label="${escape(`${boostTitle(b.type, b.params)}. ${boostLines(b.type, b.params, b.usesRemaining).join('. ')}. ${timeLeftText(b, now)}`)}">
          ${boostIconHtml(b.type, 'boost-chip-icon')}
          <span class="boost-chip-time" data-boost-time>${timerText(b, now)}</span>
        </span>`,
      )
      .join('');
    host.innerHTML = marchChips + boostChips;
    for (const chip of Array.from(host.querySelectorAll<HTMLElement>('.boost-chip[data-boost-id]'))) {
      const b = live.find((x) => x.id === chip.dataset.boostId)!;
      chip.addEventListener('pointerenter', () => showTip(chip, b));
      chip.addEventListener('pointerleave', hideTip);
      chip.addEventListener('focus', () => showTip(chip, b));
      chip.addEventListener('blur', hideTip);
    }
    for (const chip of Array.from(host.querySelectorAll<HTMLElement>('.boost-chip[data-march-id]'))) {
      const m = roads.find((x) => x.id === chip.dataset.marchId)!;
      chip.addEventListener('pointerenter', () => showMarchTip(chip, m));
      chip.addEventListener('pointerleave', hideTip);
      chip.addEventListener('focus', () => showMarchTip(chip, m));
      chip.addEventListener('blur', hideTip);
    }
  }

  /** Every second: just the march clocks and bars (re-rendering would drop a hovered chip's tooltip). A march that arrived leaves on the next full render. */
  function tickMarches(): void {
    const now = Date.now();
    let arrived = false;
    for (const m of marches) {
      const left = new Date(m.arrivalAt).getTime() - now;
      if (left <= 0) { arrived = true; continue; }
      const chip = host.querySelector(`[data-march-id="${m.id}"]`);
      const time = chip?.querySelector('[data-march-time]');
      if (time) time.textContent = formatMarchClock(left);
      const bar = chip?.querySelector<HTMLElement>('[data-march-bar]');
      if (bar) bar.style.width = `${marchProgress(m, now).toFixed(1)}%`;
      if (tipFor === m.id) {
        const tt = tip?.querySelector('[data-march-tip-time]');
        if (tt) tt.textContent = formatMarchClock(left);
      }
    }
    if (arrived) render(boostStore.get());
  }

  const unsub = boostStore.subscribe(render);
  const unsubMarches = marchStore.subscribe((list) => {
    marches = list;
    render(boostStore.get());
  });
  render(boostStore.get());
  const marchTimer = window.setInterval(tickMarches, 1000);
  // Timers re-read on a slow tick; a boost whose end passes drops out on the next one.
  const timer = window.setInterval(() => render(boostStore.get()), TICK_MS);

  return () => {
    unsub();
    unsubMarches();
    window.clearInterval(marchTimer);
    window.clearInterval(timer);
    hideTip();
    host.innerHTML = '';
  };
}
